import { useContext, useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import { useParams, useSearchParams } from 'react-router-dom';

import axios from 'axios';

import { apiFactory } from '../../../api/ApiFactory';
import { subscriptionsApi } from '../../../api/subscriptions';
import { toast } from '../../../utils/toast';
import { ApplicationContext } from '../../../contexts/ApplicationContext';
import { useFbPixel } from '../../../hooks/FbPixel';
import {
	PIX_CHARGE_STORAGE_PREFIX,
	PIX_CONSENT_STORAGE_PREFIX,
	PixCharge
} from '../components/constants/pixCharge';
import { resolveSubscriptionRedirect } from '../components/helpers/resolveSubscriptionRedirect';
import {
	AUTO_REDIRECT_DELAY_MS,
	navigateTop,
	readStoredSuccessRedirect,
	safeRedirectUrl,
	SuccessRedirectMode
} from '../components/helpers/successRedirect';
import { confirmPendingPurchase } from '../components/PageBlocks/tracking';

/**
 * Pagamento aprovado com `successRedirect` no checkout: leva o comprador para
 * o destino do lojista. `button` mostra o botão; `auto` segue sozinho depois
 * de ~2 s, com o botão como alternativa se o navegador não deixar. A URL (já
 * com o id da sessão de checkout) vem do pay e abre na MESMA aba, no topo da
 * janela, mesmo com o checkout dentro de um iframe.
 *
 * Sem URL válida não renderiza nada: quem chama cai no fluxo de sempre.
 */
export const SuccessRedirectReady = ({
	url,
	mode,
	buttonLabel,
	onAutoRedirect = navigateTop
}: {
	url?: string | null;
	mode: SuccessRedirectMode;
	buttonLabel: string;
	onAutoRedirect?: (url: string) => void;
}) => {
	const href = safeRedirectUrl(url);
	const isAuto = mode === 'auto';

	useEffect(() => {
		if (!href || !isAuto) return;
		const timer = setTimeout(
			() => onAutoRedirect(href),
			AUTO_REDIRECT_DELAY_MS
		);
		return () => clearTimeout(timer);
	}, [href, isAuto]);

	if (!href) return null;

	return (
		<>
			<h3 className="h4 text-gray-800">Pagamento aprovado!</h3>
			<p className="text-gray-600">
				{isAuto
					? 'Estamos levando você para a próxima etapa…'
					: 'Clique no botão abaixo para continuar.'}
			</p>
			<a
				href={href}
				target="_top"
				className="btn btn-primary btn-lg mt-3"
				data-testid="success-redirect"
			>
				{buttonLabel}
			</a>
		</>
	);
};

/** Vindi's `bill_paid` arrived. */
const ACTIVE_STATUS_ID = 3;

/** Consent rejected or expired: the sign-up will never complete. */
const EXPIRED_STATUS_ID = 9;

const POLL_INTERVAL_MS = 5000;

type IParams = { reference: string; platform?: string };

// Keeps a buyer out of the onboarding before paying.
export const RecurringPixCheckout = () => {
	const { reference, platform } = useParams<keyof IParams>() as IParams;
	const [searchParams] = useSearchParams();
	const { customization } = useContext(ApplicationContext);
	const { trackPurchase: trackPlatformPurchase } = useFbPixel();

	// Checkout com `successRedirect`: modo e texto vêm do submit
	// (sessionStorage); a URL vem do submit (cartão aprovado) ou do polling.
	const [successRedirect] = useState(() =>
		readStoredSuccessRedirect(reference)
	);
	const [redirectUrl, setRedirectUrl] = useState<string | null>(
		successRedirect.url
	);
	const [isConfirmed, setIsConfirmed] = useState(() => !!redirectUrl);
	const [isExpired, setIsExpired] = useState(false);
	const [isApiKeyReady, setIsApiKeyReady] = useState(false);

	// Pix Automático has no QR code: the payer opens the consent link and
	// authorizes the recurring debit in their bank app.
	const isAutomatic = searchParams.get('pix_mode') === 'automatic';

	// Cartão aguardando a aprovação chegar pelo webhook (checkout com
	// redirecionamento no motor do gateway). Não há o que pagar aqui, e o Purchase já saiu no
	// submit.
	const isCard = searchParams.get('method') === 'card';

	const consentUrl = useMemo(() => {
		try {
			return sessionStorage.getItem(
				`${PIX_CONSENT_STORAGE_PREFIX}${reference}`
			);
		} catch {
			return null;
		}
	}, [reference]);

	// Recurring resolves the api_key from the platform slug and drops it on
	// unmount (`enableDefault`), so this screen has to resolve it again before
	// polling — otherwise the request goes without the header and gets a 401.
	useEffect(() => {
		apiFactory
			.isDefaultApiKeyDefined()
			.then(async (isDefined) => {
				if (isDefined || !platform) return;

				const { data } = await axios.get<{ key: string }>(
					`${process.env.REACT_APP_CURSEDUCA_APPLICATION_DOMAIN}/platforms/slug/${platform}`
				);

				apiFactory.enableCustom(data.key);
			})
			.finally(() => setIsApiKeyReady(true));

		return () => apiFactory.enableDefault();
	}, [platform]);

	const charge = useMemo<PixCharge | null>(() => {
		try {
			const raw = sessionStorage.getItem(
				`${PIX_CHARGE_STORAGE_PREFIX}${reference}`
			);
			return raw ? (JSON.parse(raw) as PixCharge) : null;
		} catch {
			return null;
		}
	}, [reference]);

	useEffect(() => {
		if (isConfirmed || isExpired || !isApiKeyReady) return;

		let cancelled = false;

		const check = async () => {
			try {
				const subscription = await subscriptionsApi.findByReference(
					reference
				);

				if (cancelled) return;

				if (subscription.statusId === EXPIRED_STATUS_ID) {
					setIsExpired(true);
					return;
				}

				if (subscription.statusId !== ACTIVE_STATUS_ID) return;

				// Antes do `isConfirmed`: o efeito da confirmação lê a URL.
				const destination = safeRedirectUrl(
					subscription.successRedirectUrl
				);
				if (destination) setRedirectUrl(destination);
				setIsConfirmed(true);
			} catch {
				// Keep polling: the buyer may be paying right now.
			}
		};

		check();
		const interval = setInterval(check, POLL_INTERVAL_MS);

		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [reference, isConfirmed, isExpired, isApiKeyReady]);

	// Pixel da plataforma: Purchase quando o Pix confirma, só para ele, com a
	// referência da assinatura como eventID (a mesma do obrigado) e uma vez por
	// compra. Efeito próprio porque o pixel pode chegar depois da confirmação
	// (recarga da tela) — e o cartão já disparou no submit.
	useEffect(() => {
		if (!isConfirmed || isCard) return;

		trackPlatformPurchase({
			value: Number(searchParams.get('value')) || 0,
			reference
		});
	}, [isConfirmed, isCard, reference, searchParams, trackPlatformPurchase]);

	useEffect(() => {
		if (!isConfirmed) return;

		const value = Number(searchParams.get('value')) || 0;

		if (!isCard) {
			// Pixels do checkout (builder): o submit só marcou a etapa de
			// pagamento e deixou o Purchase guardado até aqui, quando o Pix
			// confirma.
			confirmPendingPurchase(reference);
		}

		// Destino do lojista: quem leva é o `SuccessRedirectReady`.
		if (redirectUrl) return;

		const redirect = searchParams.get('redirect');

		if (redirect) {
			location.replace(redirect);
			return;
		}

		const planReference = searchParams.get('plan_reference') || '';

		const { url, isOnboarding } = resolveSubscriptionRedirect({
			planReference,
			receipt: reference,
			value,
			accessToken: localStorage.getItem('access_token') || '',
			platformUrl: customization?.url,
			isComercial:
				searchParams.get('origin')?.toUpperCase() === 'COMERCIAL',
			sellerToken: searchParams.get('token')
		});

		toast.success(
			isOnboarding
				? 'Pagamento confirmado! Redirecionando para o onboarding...'
				: 'Pagamento confirmado!'
		);

		setTimeout(() => location.replace(url), 2000);
	}, [isConfirmed]);

	const copyPixCode = async () => {
		if (!charge?.qrCode) return;

		try {
			await navigator.clipboard.writeText(charge.qrCode);
			toast.success('Código Pix copiado!');
		} catch {
			toast.error('Não foi possível copiar o código.');
		}
	};

	return (
		<div className="d-flex flex-column align-items-center">
			<div className="content">
				<div className="container">
					<Card className="rounded-xl p-md-10 my-10">
						<Card.Body className="text-center">
							{isExpired ? (
								<>
									<h3 className="h4 text-gray-800">
										A autorização não foi concluída
									</h3>
									<p className="text-gray-600">
										O prazo para autorizar expirou ou a
										solicitação foi recusada no aplicativo
										do banco. Nenhum valor foi cobrado.
									</p>
									<a
										href={`${customization?.url || ''}`}
										className="btn btn-primary mt-3"
									>
										Tentar novamente
									</a>
								</>
							) : isConfirmed && redirectUrl ? (
								<SuccessRedirectReady
									url={redirectUrl}
									mode={successRedirect.mode}
									buttonLabel={successRedirect.buttonLabel}
								/>
							) : isConfirmed ? (
								<>
									<h3 className="h4 text-gray-800">
										Pagamento confirmado!
									</h3>
									<p className="text-gray-600">
										Estamos preparando seu acesso...
									</p>
									<Spinner animation="border" />
								</>
							) : (
								<>
									<h3 className="h4 text-gray-800">
										{isCard
											? 'Confirmando seu pagamento'
											: isAutomatic
											? 'Autorize no app do seu banco'
											: 'Falta pagar o Pix'}
									</h3>
									<p className="text-gray-600">
										{isCard
											? 'Isso costuma levar poucos segundos. Esta página atualiza sozinha — pode deixá-la aberta.'
											: isAutomatic
											? 'Enviamos uma solicitação de autorização para o aplicativo do seu banco. Aprove por lá e sua assinatura é liberada — esta página atualiza sozinha.'
											: 'Sua assinatura é liberada assim que o pagamento for confirmado. Esta página atualiza sozinha — pode deixá-la aberta.'}
									</p>

									{charge?.qrCodeImage && (
										<Row className="justify-content-center my-5">
											<Col xs="auto">
												<div
													className="bg-white p-4 rounded d-inline-block"
													style={{
														border: '1px solid #ebedf3'
													}}
												>
													<img
														src={charge.qrCodeImage}
														alt="QR code do Pix"
														// pixelated: the Vindi QR
														// is a low-res PNG and
														// smoothing it while
														// scaling up breaks
														// readers.
														style={{
															width: '20rem',
															maxWidth: '100%',
															imageRendering:
																'pixelated'
														}}
													/>
												</div>
											</Col>
										</Row>
									)}

									{charge?.qrCode && (
										<button
											type="button"
											className="btn btn-outline-primary mb-3"
											onClick={copyPixCode}
										>
											Copiar código Pix
										</button>
									)}

									{consentUrl && (
										<>
											<div>
												<a
													href={consentUrl}
													target="_blank"
													rel="noopener noreferrer"
													className="btn btn-primary btn-lg"
												>
													Autorizar no meu banco
												</a>
											</div>
											<p className="text-gray-600 font-size-sm mt-4 mb-0">
												Você tem 60 minutos para
												concluir a autorização.
											</p>
										</>
									)}

									{charge?.paymentUrl && (
										<div>
											<a
												href={charge.paymentUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="btn btn-primary"
											>
												Abrir página de pagamento
											</a>
										</div>
									)}

									{!charge &&
										!consentUrl &&
										!isAutomatic &&
										!isCard && (
											<p className="text-gray-600">
												Enviamos a cobrança para o seu
												e-mail. Assim que o pagamento
												for identificado, esta página
												segue automaticamente.
											</p>
										)}

									<div className="mt-8 d-flex align-items-center justify-content-center">
										<Spinner
											animation="border"
											size="sm"
											className="mr-3"
										/>
										<span className="text-gray-600">
											{isAutomatic
												? 'Aguardando sua autorização'
												: isCard
												? 'Aguardando a aprovação do pagamento'
												: 'Aguardando confirmação do pagamento'}
										</span>
									</div>
								</>
							)}
						</Card.Body>
					</Card>
				</div>
			</div>
		</div>
	);
};
